import React, { useState, useEffect, useMemo } from 'react';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DynamicIcon } from './icons.jsx';

/**
 * Maps activePage IDs to their human-readable labels and parent categories.
 */
const PAGE_CONFIG = {
   home: { label: 'Home', category: 'Navigation' },
   landing: { label: 'Home', category: 'Navigation' },
   dashboard: { label: 'Dashboard', category: 'Navigation' },
   logs: { label: 'Execution Status', category: 'Navigation' },

   // Creator Studio
   flows: { label: 'Flows', category: 'Creator Studio' },
   'ai-insight': { label: 'Agentic Insights', category: 'Creator Studio' },
   dashboards: { label: 'Dashboard Studio', category: 'Creator Studio' },
   business_apps: { label: 'Business Apps', category: 'Creator Studio' },
   automations: { label: 'Scheduler', category: 'Creator Studio' },
   alerts: { label: 'Alert Rules', category: 'Creator Studio' },
   'semantic-kb': { label: 'Knowledge base', category: 'Creator Studio' },

   // App Studio
   'data-workspace': { label: 'Unified Data Workspace', category: 'App Studio' },
   'dq-profiler': { label: 'Data Profiler', category: 'App Studio' },
   datasources: { label: 'Data Sources', category: 'App Studio' },
   datamodels: { label: 'Data Models', category: 'App Studio' },
   datasets: { label: 'Datasets', category: 'App Studio' },
   DatasetsPage: { label: 'Datasets', category: 'App Studio' },
   'worklist-studio': { label: 'Worklist Studio', category: 'App Studio' },
   'worklist-editor': { label: 'Worklist Editor', category: 'App Studio' },
   'worklist-runner': { label: 'Worklist Runner', category: 'App Studio' },
   ml_models: { label: 'ML Models', category: 'App Studio' },

   // Administration
   partners: { label: 'Partners', category: 'Administration' },
   tenants: { label: 'Tenants', category: 'Administration' },
   users: { label: 'Users', category: 'Administration' },
   roles_permissions: { label: 'Roles & Permissions', category: 'Administration' },
   plans: { label: 'Plans & Tiers', category: 'Administration' },
   credits: { label: 'Credit Management', category: 'Administration' },
   support: { label: 'Support Tickets', category: 'Administration' },
   demo_management: { label: 'Public Demos', category: 'Administration' },
   platform_settings: { label: 'Settings', category: 'Administration' },
   audit_logs: { label: 'Audit Logs', category: 'Administration' },
   administration: { label: 'Administration', category: 'Administration' },

   // Settings
   settings: { label: 'Tenant Settings', category: 'Settings' },
   'my-trash': { label: 'Recycle Bin', category: 'Settings' },
   'admin-trash': { label: 'Trash Bin', category: 'Settings' },
   profile: { label: 'My Profile', category: 'Settings' },
   about: { label: 'About', category: 'Settings' },

   // Other
   'my-apps': { label: 'My Apps', category: 'Navigation' },
   app_runner: { label: 'App Runner', category: 'Navigation' },
   tenant_app_master: { label: 'My Apps', category: 'Navigation' },
   tenant_app_section: { label: 'My Apps', category: 'Navigation' },
   tasks: { label: 'Task Center', category: 'Navigation' }
};

export default function GlobalBreadcrumbs({
   activePage,
   viewingTenantId,
   viewingPartnerId,
   onboardingTenantId,
   dashboardEditorState,
   editingContentId,
   viewingAsset,
   previewContentId,
   tenantAppRunnerProps,
   allAppBundles,
   allSections,
   currentUser,
   isTenantUser,
   handleBack,
   handleNavigate,
   setAidasAppsExpanded,
   setPublishedAppsExpanded,
   setMyAppsExpanded
}) {
   const [dynamicTitle, setDynamicTitle] = useState(null);
   const navigate = useNavigate();
   const location = useLocation();

   // Listen for dynamically dispatched page/item titles
   useEffect(() => {
      const handleSetTitle = (e) => {
         if (e.detail) {
            setDynamicTitle(e.detail);
         }
      };
      window.addEventListener('set-breadcrumb-title', handleSetTitle);
      return () => window.removeEventListener('set-breadcrumb-title', handleSetTitle);
   }, []);

   // Reset dynamic title whenever activePage, pathname or dynamic IDs change
   useEffect(() => {
      setDynamicTitle(null);
   }, [
      activePage,
      location.pathname,
      viewingTenantId,
      viewingPartnerId,
      onboardingTenantId,
      dashboardEditorState?.dashboardId,
      editingContentId,
      viewingAsset?.id,
      previewContentId,
      tenantAppRunnerProps?.appUid,
      tenantAppRunnerProps?.contentUid
   ]);

   const breadcrumbs = useMemo(() => {
      const items = [];

      // Always start with Home
      const homePage = isTenantUser ? 'landing' : 'dashboard';
      items.push({
         label: 'Home',
         onClick: () => handleNavigate(homePage)
      });

      // 1. Tenant App Runner Mode
      if (activePage === 'tenant_app_runner' && tenantAppRunnerProps) {
         items.push({
            label: 'My Apps',
            onClick: () => handleNavigate('my-apps')
         });

         if (tenantAppRunnerProps.sectionTitle) {
            items.push({
               label: tenantAppRunnerProps.sectionTitle,
               onClick: () => {
                  const title = tenantAppRunnerProps.sectionTitle;
                  if (title === 'AIDAS Apps') {
                     setAidasAppsExpanded?.(true);
                  } else if (title === 'Published Apps') {
                     setPublishedAppsExpanded?.(true);
                  } else if (title === 'My Workspace') {
                     setMyAppsExpanded?.(true);
                  }
                  handleNavigate('my-apps');
               }
            });
         }

         const bundle = allAppBundles?.find(b => b.id === tenantAppRunnerProps.appUid);
         const bundleName = bundle?.name || 'App';
         items.push({
            label: bundleName,
            onClick: () => navigate(
               tenantAppRunnerProps.tenantId
                  ? `/${tenantAppRunnerProps.tenantId}/${tenantAppRunnerProps.appUid}`
                  : `/tenant/app/${tenantAppRunnerProps.appUid}`
            )
         });

         if (tenantAppRunnerProps.contentUid || dynamicTitle) {
            const content = bundle?.contents?.find(c => c.id === tenantAppRunnerProps.contentUid);
            const contentName = content?.name || dynamicTitle || 'Run';
            items.push({
               label: contentName
            });
         }

         return items;
      }

      // 2. Dashboards (View/Edit/New)
      if (activePage === 'dashboards' || activePage === 'myapps_dashboard') {
         const config = PAGE_CONFIG[activePage] || { label: 'Dashboard Studio', category: 'Creator Studio' };


         items.push({
            label: config.label,
            onClick: () => {
               handleBack();
               handleNavigate(activePage);
            }
         });

         if (dashboardEditorState) {
            if (dashboardEditorState.mode === 'new') {
               items.push({
                  label: 'New Dashboard'
               });
            } else {
               // Dynamic dashboard name lookup
               const dashboardId = dashboardEditorState.dashboardId;
               let dashboardName = dynamicTitle || 'Dashboard';

               // Attempt to find dashboard name from allSections or allAppBundles
               if (allSections?.dashboards) {
                  const db = allSections.dashboards.find(d => d.id === dashboardId);
                  if (db) dashboardName = db.name;
               }
               if (dashboardName === 'Dashboard' && allAppBundles) {
                  for (const b of allAppBundles) {
                     const db = b.contents?.find(c => c.id === dashboardId && c.type === 'dashboard');
                     if (db) {
                        dashboardName = db.name;
                        break;
                     }
                  }
               }

               items.push({
                  label: dashboardName,
                  onClick: dashboardEditorState.mode === 'edit' ? () => {
                     handleBack();
                     handleNavigate(activePage);
                     // set viewing mode
                     window.history.pushState(null, '', `/dashboards/${dashboardId}/view`);
                     window.dispatchEvent(new PopStateEvent('popstate'));
                  } : null
               });

               if (dashboardEditorState.mode === 'edit') {
                  items.push({
                     label: 'Edit'
                  });
               }
            }
         }

         return items;
      }

      // 3. Platform Admin - Viewing/Onboarding Tenant
      if (activePage === 'tenants') {

         items.push({
            label: 'Tenants',
            onClick: (viewingTenantId || onboardingTenantId) ? () => {
               handleBack();
               handleNavigate('tenants');
            } : null
         });

         if (onboardingTenantId) {
            items.push({ label: 'Onboard' });
         } else if (viewingTenantId) {
            items.push({ label: dynamicTitle || 'Tenant Details' });
         }
         return items;
      }

      // 4. Platform Admin - Viewing Partner
      if (activePage === 'partners') {

         items.push({
            label: 'Partners',
            onClick: viewingPartnerId ? () => {
               handleBack();
               handleNavigate('partners');
            } : null
         });

         if (viewingPartnerId) {
            items.push({ label: dynamicTitle || 'Partner Details' });
         }
         return items;
      }

      // 5. Platform Admin - Business Apps Asset Detail or Content Editor
      if (activePage === 'business_apps') {

         items.push({
            label: 'Business Apps',
            onClick: (viewingAsset || editingContentId) ? () => {
               handleBack();
               handleNavigate('business_apps');
            } : null
         });

         if (viewingAsset) {
            items.push({ label: viewingAsset.name || 'Asset Details' });
         } else if (editingContentId) {
            items.push({ label: dynamicTitle || 'Edit Content' });
         }
         return items;
      }

      // 6. Generic Pages
      const config = PAGE_CONFIG[activePage];
      if (config) {
         items.push({
            label: config.label,
            onClick: dynamicTitle ? () => {
               handleBack();
               handleNavigate(activePage);
            } : undefined
         });
         if (dynamicTitle) {
            items.push({ label: dynamicTitle });
         }
      } else {
         // Fallback for uncaught activePage names
         const cleanName = activePage
            ? activePage.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            : '';
         if (cleanName) {
            items.push({
               label: cleanName,
               onClick: dynamicTitle ? () => {
                  handleBack();
                  handleNavigate(activePage);
               } : undefined
            });
            if (dynamicTitle) {
               items.push({ label: dynamicTitle });
            }
         }
      }

      return items;
   }, [
      activePage,
      viewingTenantId,
      viewingPartnerId,
      onboardingTenantId,
      dashboardEditorState,
      editingContentId,
      viewingAsset,
      previewContentId,
      tenantAppRunnerProps,
      allAppBundles,
      allSections,
      isTenantUser,
      dynamicTitle,
      handleBack,
      handleNavigate,
      setAidasAppsExpanded,
      setPublishedAppsExpanded,
      setMyAppsExpanded
   ]);

   // Do not show breadcrumbs on Home/Dashboard landing screens to keep the design clean
   if (activePage === 'home' || activePage === 'landing' || activePage === 'dashboard' || breadcrumbs.length <= 1) {
      return null;
   }

   return (
      <Breadcrumbs
         separator={
            <DynamicIcon
               name="ChevronRight"
               className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 shrink-0"
            />
         }
         sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif',
            color: 'text.secondary',
            '& .MuiBreadcrumbs-separator': {
               mx: 1
            }
         }}
      >
         {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;

            if (isLast || !crumb.onClick) {
               return (
                  <Typography
                     key={idx}
                     sx={{
                        fontSize: '13px',
                        fontWeight: isLast ? 600 : 400,
                        color: isLast ? 'text.primary' : 'text.secondary',
                        cursor: 'default',
                        userSelect: 'none'
                     }}
                  >
                     {crumb.label}
                  </Typography>
               );
            }

            return (
               <Link
                  key={idx}
                  underline="hover"
                  color="inherit"
                  onClick={(e) => {
                     e.preventDefault();
                     crumb.onClick();
                  }}
                  sx={{
                     fontSize: '13px',
                     fontWeight: 400,
                     cursor: 'pointer',
                     transition: 'color 0.2s',
                     '&:hover': {
                        color: 'primary.main'
                     }
                  }}
               >
                  {crumb.label}
               </Link>
            );
         })}
      </Breadcrumbs>
   );
}
